import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, LabelList } from 'recharts';
import { BookOpen, Target, Clock, TrendingUp, Zap, Users, CheckCircle2, XCircle, ChartBar, Trophy, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api  from '../api';
import { convertCorrectAnswersToSAT, PIE_COLORS } from '../utils/SATscoreCalculator';
import { ThemeContext } from '../ThemeContext';

// const RequirementsCheck = ({ title, requirementsList }) => (
//     <div>
//       <h4 className="font-semibold mb-2 text-gray-600">{title}</h4>
//       <ul className="space-y-1 text-sm">
//         {requirementsList.map(req => (
//           <li key={req.name} className="flex justify-between items-center py-1 border-b border-gray-100">
//             <span className={`${req.isMet ? 'text-gray-700' : 'text-red-500'}`}>{req.name}</span>
//             <div className="flex items-center space-x-2 text-xs">
//                 <span className="flex items-center text-green-600 font-semibold">
//                     {req.correct}
//                     <CheckCircle2 size={14} className="ml-1" />
//                 </span>
//                 <span className="flex items-center text-red-600 font-semibold">
//                     {req.incorrect}
//                     <XCircle size={14} className="ml-1" />
//                 </span>
//                 <span className={`font-mono font-bold ${req.isMet ? 'text-gray-500' : 'text-red-500'}`}>
//                     ({req.current}/{req.required})
//                 </span>
//             </div>
//           </li>
//         ))}
//       </ul>
//     </div>
//   );

// Function to convert degrees to radians
const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
  const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
  return {
    x: centerX + (radius * Math.cos(angleInRadians)),
    y: centerY + (radius * Math.sin(angleInRadians))
  };
};

// Function to draw a pie slice SVG path
const describeArc = (x, y, radius, startAngle, endAngle) => {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return [
    "M", start.x, start.y,
    "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
    "L", x, y,
    "L", start.x, start.y
  ].join(" ");
};

// Component for a small pie chart
// const SmallPieChart = ({ correct, incorrect, unanswered }) => {
//   const total = correct + incorrect + unanswered;
//   if (total === 0) return <div className="text-gray-400 text-xs">No data</div>;

//   const correctAngle = (correct / total) * 360;
//   const incorrectAngle = (incorrect / total) * 360;
//   const unansweredAngle = (unanswered / total) * 360;

//   return (
//     <div className="flex items-center justify-center -mr-2"> {/* Negative margin to slightly overlap */}
//       <svg width="40" height="40" viewBox="0 0 40 40">
//         <circle cx="20" cy="20" r="18" fill="#e0e7ff" /> {/* Background circle */}
//         {correct > 0 && (
//           <path
//             d={describeArc(20, 20, 18, 0, correctAngle)}
//             fill="#22c55e" // Green for correct
//           />
//         )}
//         {incorrect > 0 && (
//           <path
//             d={describeArc(20, 20, 18, correctAngle, correctAngle + incorrectAngle)}
//             fill="#ef4444" // Red for incorrect
//           />
//         )}
//         {unanswered > 0 && (
//           <path
//             d={describeArc(20, 20, 18, correctAngle + incorrectAngle, correctAngle + incorrectAngle + unansweredAngle)}
//             fill="#a0aec0" // Gray for unanswered
//           />
//         )}
//       </svg>
//       <div className="ml-1 flex flex-col text-[10px] font-semibold text-gray-700">
//         <span className="flex items-center whitespace-nowrap">
//           <span className="w-2 h-2 rounded-full bg-emerald-500 mr-1"></span> {correct} Correct
//         </span>
//         <span className="flex items-center whitespace-nowrap">
//           <span className="w-2 h-2 rounded-full bg-red-500 mr-1"></span> {incorrect} Wrong
//         </span>
//         <span className="flex items-center whitespace-nowrap">
//           <span className="w-2 h-2 rounded-full bg-gray-400 mr-1"></span> {unanswered} Unanswered
//         </span>
//       </div>
//     </div>
//   );
// };

// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border border-white/20 dark:border-gray-700/50 ${className}`}
      style={{
        boxShadow: isHovered
          ? '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 40px rgba(0, 0, 0, 0.1)'
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
      }}
    >
      {children}
    </motion.div>
  );
};

// Updated MetricCard component with animations
const MetricCard = ({ title, value, unit = '', icon: Icon, color = 'text-gray-800', children, delay = 0 }) => (
  <Card delay={delay}>
    <div className="flex flex-row items-center justify-center h-full w-full gap-4">
      {Icon && (
        <motion.div
          whileHover={{ rotate: 360 }}
          transition={{ duration: 0.6 }}
        >
          <Icon size={32} style={{ color: '#a6a6a6' }} />
        </motion.div>
      )}
      <div className="flex flex-col items-start justify-center">
        <h3 className="text-gray-500 dark:text-gray-400 text-sm font-semibold uppercase mb-1 font-zonaProRegular">{title}</h3>
        <motion.p
          className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-200 mb-1 font-zonaProUltra"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: delay + 0.2 }}
        >
          {value}{unit}
        </motion.p>
        {children}
      </div>
    </div>
  </Card>
);

// Pie chart colors for correct, wrong, unanswered
const PIE_COLORS_O = ['#34c759', '#ef4444', '#fbbf24']; // green, red, orange

// Custom PieChart Card for Overall Performance
const OverallPerformancePieCard = ({ correct, wrong, unanswered, total, delay = 0 }) => {
  const pieData = [
    { name: 'Correct', value: correct },
    { name: 'Wrong', value: wrong },
    { name: 'Unanswered', value: unanswered },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      className="relative backdrop-blur-xl bg-gradient-to-br from-indigo-500 to-purple-500 dark:from-indigo-600 dark:to-purple-600 rounded-3xl shadow-2xl p-6 flex flex-col h-full w-full min-w-[260px] min-h-[260px] border border-white/20 dark:border-gray-700/50"
    >
    <div className="flex flex-row items-stretch h-full w-full gap-4 sm:gap-6">
      <div className="flex flex-col justify-center min-w-[120px] max-w-[180px] flex-shrink-0 gap-3">
        <div className="flex items-center mb-2">
          <span className="font-zonaProRegular text-white text-lg font-semibold">Total Questions</span>
        </div>
        <div className="flex items-center mb-4">
          <span className="ml-2 font-zonaProUltra text-3xl font-bold text-white">{total}</span>
        </div>
        <span className="flex items-center font-zonaProRegular text-green-200 font-semibold text-lg"><span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: '#34c759' }}></span>{correct} correct</span>
        <span className="flex items-center font-zonaProRegular text-red-200 font-semibold text-lg"><span className="w-3 h-3 rounded-full bg-[#ef4444] mr-2"></span>{wrong} wrong</span>
        <span className="flex items-center font-zonaProRegular text-yellow-200 font-semibold text-lg"><span className="w-3 h-3 rounded-full bg-[#fbbf24] mr-2"></span>{unanswered} unanswered</span>
      </div>
      {/* Right: Pie Chart */}
      <div className="flex flex-1 items-center justify-center min-w-0">
        <div className="w-full max-w-[220px] sm:max-w-[260px]">
          <div className="w-full h-[200px] sm:h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius="65%"   // scales with container
                  outerRadius="90%"   // scales with container
                  paddingAngle={2}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                  stroke="none"
                  strokeWidth={0}
                  activeDot={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${entry.name}`}
                      fill={PIE_COLORS_O[index % PIE_COLORS_O.length]}
                      stroke="none"
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [`${value} ${name}`]}
                  labelStyle={{ color: '#333' }}
                  contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.5rem' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

    </div>
    </motion.div>
  );
};

// --- New: Estimated SAT Score Card ---
const ScoreCard = ({
  locked,
  score,
  title,
  message,
  subcategories,
  totalRequired,
  totalAnswered,
  missingSubcategories,
  color,
  allComplete,
  isMath,
  delay = 0,
}) => (
  <Card delay={delay}>
    <div className="flex items-center mb-2">
      {locked ? (
        <motion.span
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full px-2 py-1 text-sm font-semibold flex items-center mr-2 font-zonaProRegular"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" /></svg>
          {title} (Locked)
        </motion.span>
      ) : (
        <motion.span
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full px-2 py-1 text-sm font-semibold flex items-center mr-2 font-zonaProRegular"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg>
          {title}
        </motion.span>
      )}
      {!locked && (
        <motion.span
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="text-lg font-bold text-green-700 dark:text-green-400 ml-2 font-zonaProUltra"
        >
          {score}
        </motion.span>
      )}
    </div>
    {score !== null && (
      <div className="w-full flex justify-center mb-2">
        <ResponsiveContainer width="95%" height={80}>
          <BarChart
            layout="vertical"
            data={[{ name: title.split(' ')[0], score }]}
            margin={{ top: 0, right: 20, left: 20, bottom: 0 }}
          >
            {/* Show the XAxis with ticks and a visible line */}
            <XAxis
              type="number"
              domain={[150, 800]}
              ticks={[200, 300, 400, 500, 600, 700, 800]}
              axisLine={true}
              tickLine={true}
              tick={{ fill: '#a78bfa', fontSize: 12 }}
            />
            {/* Optionally, show the YAxis label */}
            <YAxis
              type="category"
              dataKey="name"
              width={60}
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#a78bfa', fontSize: 14 }}
            />
            <Tooltip formatter={(value) => `${value}`} />
            <Bar dataKey="score" name="Estimated Score" barSize={24} minPointSize={10} >
              <Cell fill={isMath ? '#60a5fa' : '#facc15'} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}
    <div className="text-gray-600 dark:text-gray-400 mb-4 text-sm font-zonaProRegular">{message}</div>
    <div className="overflow-x-auto">
      <table className="w-full text-center rounded-2xl overflow-hidden border-separate border-spacing-0 dark:bg-gray-800/50" style={{ height: 'auto' }}>
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-700 text-black dark:text-gray-200 text-base font-zonaProRegular">
            <th className="py-2 px-2 font-bold">Subcategory</th>
            <th className="py-2 px-2 font-bold">Correct</th>
            <th className="py-2 px-2 font-bold">Incorrect</th>
            <th className="py-2 px-2 font-bold">Skipped</th>
            <th className="py-2 px-2 font-bold">Total</th>
            <th className="py-2 px-2 font-bold">Required</th>
          </tr>
        </thead>
        <tbody>
          {subcategories.map((cat, idx) => (
            <motion.tr
              key={cat.name}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className={idx === subcategories.length-1 ? "border-t-2 font-bold dark:border-gray-600":"border-b last:border-b-0 dark:border-gray-700"}
            >
              <td className="py-2 px-2 text-left font-medium text-gray-800 dark:text-gray-200">{cat.name}</td>
              <td className="py-2 px-2 text-green-600 dark:text-green-400 font-medium">{cat.correct}</td>
              <td className="py-2 px-2 text-red-500 dark:text-red-400 font-medium">{cat.incorrect}</td>
              <td className="py-2 px-2 text-yellow-500 dark:text-yellow-400 font-medium">{cat.unanswered}</td>
              <td className="py-2 px-2 font-medium text-gray-800 dark:text-gray-200">{cat.total}</td>
              <td className="py-2 px-2 font-medium text-gray-800 dark:text-gray-200">{cat.required}</td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
    {/* Remarks */}
    <div className="mt-4 text-sm flex flex-col gap-1">
      {allComplete ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-left text-green-600 dark:text-green-400 font-semibold font-zonaProRegular"
        >
          You've completed all {totalRequired} {title.split(' ')[0]} questions.
        </motion.div>
      ) : (
        <>
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-start text-black dark:text-gray-300"
          >
            <span className="mt-0.5"><XCircle size={16} className="text-red-500 dark:text-red-400" /></span>
            <span className="ml-2 font-zonaProRegular">You've answered {subcategories.find(cat => cat.name.includes('Total')).total} out of {totalRequired} questions.</span>
          </motion.div>
          {missingSubcategories.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="flex items-start text-black dark:text-gray-300"
            >
              <span className="mt-0.5"><XCircle size={16} className="text-red-500 dark:text-red-400" /></span>
              <span className="ml-2 text-left font-zonaProRegular">
                Answer {missingSubcategories.map(m => `${m.missing} more in ${m.name}`).join(', ')} to unlock your {title.split(' ')[0]} score!
              </span>
            </motion.div>
          )}
        </>
      )}
    </div>
  </Card>
);

// Main App component
const AnalyticsPage = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const user = JSON.parse(localStorage.getItem('sat_user'));
  const user_id = user.user_id;
  const navigate = useNavigate();
  const [answerHistory, setAnswerHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalTimeSpent, setTotalTimeSpent] = useState(0);
  const [dailySatScores, setDailySatScores] = useState([]);
  const [selectedDayStats, setSelectedDayStats] = useState(null);
  const [streaks, setStreaks] = useState({ longest_streak: 0, current_streak: 0 });
  const [timeRange, setTimeRange] = useState("7d");
  const [highestSatScore, setHighestSatScore] = useState(null);

  const handleRangeChange = (e) => {
    setTimeRange(e.target.value);
  };

  const {
    categoryPerformance,
    strongestZones,
    weakestZones,
    dailyAccuracyTrend,
    weeklyAccuracyTrend,
    recentAccuracyTrend,
    monthlyAccuracyTrend,
    recentAccuracyTrend_month,
    monthlyAccurayTrendB,
    recentAccuracyTrend_months,
    yearlyAccuracyTrend,
    recentAccuracyTrend_year,
    totalTimePerCategory,
    engagement,
    correctCount,
    incorrectCount,
    unansweredCount,
  } = useMemo(() => {
    if (!answerHistory || answerHistory.length === 0) {
      return {
        categoryPerformance: [],
        strongestZones: [],
        weakestZones: [],
        dailyAccuracyTrend: [],
        totalTimePerCategory: [],
        engagement: {
          numberOfActiveDays: 0,
        },
        correctCount: 0,
        incorrectCount: 0,
        unansweredCount: 0,
      };
    }

    // Category Performance
    const categoryData = answerHistory.reduce((acc, ans) => {
      const category = ans.content_name || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = {
          name: category,
          correct: 0,
          incorrect: 0,
          unanswered: 0,
          totalTime: 0,
          questionCount: 0,
        };
      }

      acc[category].questionCount++;
      acc[category].totalTime += ans.time_spent || 0;
      if (ans.question_status === 'correct') acc[category].correct++;
      else if (ans.question_status === 'incorrect') acc[category].incorrect++;
      else acc[category].unanswered++;
      
      return acc;
    }, {});
    console.log(categoryData, 'categoryData');
    
    const categoryPerformance = Object.values(categoryData).map(cat => {
      if (cat.name === 'Sentence Completion') cat.name = 'Vocablary In Context';
      const attempted = cat.correct + cat.incorrect + cat.unanswered;
      const accuracy = attempted > 0 ? (cat.correct / attempted) * 100 : 0;
      const avgTimePerQuestion = cat.questionCount > 0 ? cat.totalTime / cat.questionCount : 0;
      return {...cat, attempted, accuracy, avgTimePerQuestion};
    }).sort((a, b) => a.name.localeCompare(b.name));

    const sortedByAccuracy = [...categoryPerformance].sort((a, b) => b.accuracy - a.accuracy);
    const strongestZones = sortedByAccuracy.slice(0, 2);
    console.log("strongestZones", strongestZones);
    if (strongestZones){
      if (strongestZones[0].name === 'Sentence Completion') strongestZones[0].name = 'Vocablary In Context';
      if (strongestZones[1]){
        if (strongestZones[1].name === 'Sentence Completion') strongestZones[1].name = 'Vocablary In Context';
      }
    }
    const weakestZones = sortedByAccuracy.slice(-2).reverse();
    if (weakestZones){
      if (weakestZones[0].name === 'Sentence Completion') weakestZones[0].name = 'Vocablary In Context';
      if (weakestZones[1]){
        if (weakestZones[1].name === 'Sentence Completion') weakestZones[1].name = 'Vocablary In Context';
      }
    }

    // Daily Accuracy Trend
    const dailyData = answerHistory.reduce((acc, ans) => {
      if (!ans.created_date) return acc;
      const date = new Date(ans.created_date).toISOString().split('T')[0];
      if (!acc[date]) {
        acc[date] = { date, correct: 0, incorrect: 0, unanswered: 0 };
      }
      if (ans.question_status === 'correct') acc[date].correct++;
      else if (ans.question_status === 'incorrect') acc[date].incorrect++;
      else if (ans.question_status === 'unanswered') acc[date].unanswered++;
      return acc;
    }, {});

    const dailyAccuracyTrend = Object.values(dailyData)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map(day => {
        const totalAttempted = day.correct + day.incorrect+day.unanswered;
        const accuracy = totalAttempted > 0 ? (day.correct / totalAttempted) * 100 : 0;
        return {date: new Date(day.date), accuracy};
      });

    const now = new Date();
    const sevenDaysAgo = new Date();
    const monthAgo = new Date();
    const monthsAgo = new Date();
    const yearAgo = new Date();
    sevenDaysAgo.setDate(now.getDate() - 7);
    monthAgo.setDate(now.getDate() - 30);
    monthsAgo.setDate(now.getDate() - 90);
    yearAgo.setDate(now.getDate() - 365);

    // Past Week: Show last 7 days with labels for all days, but only data for attempted days
    const generatePastWeekData = () => {
      const weekData = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(now.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayData = dailyAccuracyTrend.find(entry => 
          entry.date.toISOString().split('T')[0] === dateStr
        );
        
        weekData.push({
          date: date,
          label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          accuracy: dayData ? dayData.accuracy : null,
          hasData: !!dayData
        });
      }
      return weekData;
    };

    // Past Month: Show 4 weeks with weekly averages
    const generatePastMonthData = () => {
      const weekData = [];
      for (let week = 3; week >= 0; week--) {
        const weekStart = new Date();
        weekStart.setDate(now.getDate() - (week * 7) - 6);
        const weekEnd = new Date();
        weekEnd.setDate(now.getDate() - (week * 7));
        
        const weekEntries = dailyAccuracyTrend.filter(entry =>
          entry.date >= weekStart && entry.date <= weekEnd
        );
        
        const weekAverage = weekEntries.length > 0 
          ? weekEntries.reduce((sum, entry) => sum + entry.accuracy, 0) / weekEntries.length 
          : null;
        
        weekData.push({
          date: weekEnd,
          label: `Week${4-week}`,
          accuracy: weekAverage,
          hasData: weekEntries.length > 0
        });
      }
      return weekData;
    };

    // Past 3 Months: Show monthly data for attempted months
    const generatePast3MonthsData = () => {
      const monthData = [];
      for (let month = 2; month >= 0; month--) {
        const monthStart = new Date(now.getFullYear(), now.getMonth() - month, 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() - month + 1, 0);
        
        const monthEntries = dailyAccuracyTrend.filter(entry =>
          entry.date >= monthStart && entry.date <= monthEnd
        );
        
        const monthAverage = monthEntries.length > 0 
          ? monthEntries.reduce((sum, entry) => sum + entry.accuracy, 0) / monthEntries.length 
          : null;
        
        monthData.push({
          date: monthEnd,
          label: monthEnd.toLocaleDateString('en-US', { month: 'short' }),
          accuracy: monthAverage,
          hasData: monthEntries.length > 0
        });
      }
      return monthData;
    };

    // Past Year: Show 12 months with monthly data
    const generatePastYearData = () => {
      const yearData = [];
      for (let month = 11; month >= 0; month--) {
        const monthStart = new Date(now.getFullYear(), now.getMonth() - month, 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() - month + 1, 0);
        
        const monthEntries = dailyAccuracyTrend.filter(entry =>
          entry.date >= monthStart && entry.date <= monthEnd
        );
        
        const monthAverage = monthEntries.length > 0 
          ? monthEntries.reduce((sum, entry) => sum + entry.accuracy, 0) / monthEntries.length 
          : null;
        
        yearData.push({
          date: monthEnd,
          label: monthEnd.toLocaleDateString('en-US', { month: 'short' }),
          accuracy: monthAverage,
          hasData: monthEntries.length > 0
        });
      }
      return yearData;
    };

    const pastWeekData = generatePastWeekData();
    const pastMonthData = generatePastMonthData();
    const past3MonthsData = generatePast3MonthsData();
    const pastYearData = generatePastYearData();

    // Keep the old variables for backward compatibility but update them
    const weeklyAccuracyTrend = pastWeekData.filter(entry => entry.hasData);
    const recentAccuracyTrend = pastWeekData;

    const monthlyAccuracyTrend = pastMonthData.filter(entry => entry.hasData);
    const recentAccuracyTrend_month = pastMonthData;

    const monthlyAccurayTrendB = past3MonthsData.filter(entry => entry.hasData);
    const recentAccuracyTrend_months = past3MonthsData;

    const yearlyAccuracyTrend = pastYearData.filter(entry => entry.hasData);
    const recentAccuracyTrend_year = pastYearData;

    // Time allocation
    const totalTimePerCategory = categoryPerformance.map(cat => ({
      name: cat.name,
      value: cat.totalTime,
      avgTimePerQuestion: cat.avgTimePerQuestion
    }));

    // Engagement
    const numberOfActiveDays = new Set(answerHistory.map(a => new Date(a.created_date).toLocaleDateString())).size;

    return {
      categoryPerformance,
      strongestZones,
      weakestZones,
      dailyAccuracyTrend,
      weeklyAccuracyTrend,
      recentAccuracyTrend,
      monthlyAccuracyTrend,
      recentAccuracyTrend_month,
      monthlyAccurayTrendB,
      recentAccuracyTrend_months,
      yearlyAccuracyTrend,
      recentAccuracyTrend_year,
      totalTimePerCategory,
      engagement: {
        numberOfActiveDays,
      },
      correctCount: answerHistory.filter(a => a.question_status === 'correct').length,
      incorrectCount: answerHistory.filter(a => a.question_status === 'incorrect').length,
      unansweredCount: answerHistory.filter(a => a.question_status === 'unanswered').length,
    };
  }, [answerHistory]);

  const latestScores = dailySatScores.length > 0 ? dailySatScores[dailySatScores.length - 1] : null;
  let satScoreValue = '--';
  if (latestScores && latestScores.mathScore && latestScores.englishScore) {
    satScoreValue = latestScores.mathScore + latestScores.englishScore;
  }


  useEffect(() => {
    setIsLoading(true);
    const fetchAnswerHistory = api.get(`/api/user/answer_history/`);
    const fetchScores = api.get(`/api/scores/`);
    //const fetchTimeSpent = api.get(`/api/get_time_spent/`);
    const fetchUserProfile = api.get(`/api/user/get_user_profile/`);

    Promise.all([fetchAnswerHistory, fetchScores, fetchUserProfile])
      .then(([answerHistoryRes, scoresRes, userProfileRes]) => {
        setAnswerHistory(answerHistoryRes.data.answer_history || []);
        // const timeData = timeSpentRes.data;
        // setTotalTimeSpent(
        //     (timeData.all_content || 0) + (timeData.all_math || 0) + (timeData.all_english || 0) + 
        //     (timeData.advanced_math || 0) + (timeData.algebra || 0) + (timeData.problem_solving_and_data_analysis || 0) + 
        //     (timeData.geometry_and_trigonometry || 0) + (timeData.reading || 0) + (timeData.writing || 0) + 
        //     (timeData.sentence_completion || 0)
        // );

        const dailyData = scoresRes.data.daily || [];
        const processedDailyScores = dailyData.map(data => {
            const mathReqs = [
                 { name: 'Algebra', current: data.algebra.total, correct: data.algebra.correct, incorrect: data.algebra.incorrect,unanswered: data.algebra.unanswered, required: 15, isMet: data.algebra.total >= 15 },
                { name: 'Advanced Math', current: data.advanced_math.total, correct: data.advanced_math.correct, incorrect: data.advanced_math.incorrect,unanswered: data.advanced_math.unanswered, required: 15, isMet: data.advanced_math.total >= 15 },
                { name: 'Problem Solving', current: data.problem_solving_and_data_analysis.total, correct: data.problem_solving_and_data_analysis.correct, incorrect: data.problem_solving_and_data_analysis.incorrect,unanswered: data.problem_solving_and_data_analysis.unanswered, required: 7, isMet: data.problem_solving_and_data_analysis.total >= 7 },
                { name: 'Geometry', current: data.geometry_and_trigonometry.total, correct: data.geometry_and_trigonometry.correct, incorrect: data.geometry_and_trigonometry.incorrect,unanswered: data.geometry_and_trigonometry.unanswered, required: 7, isMet: data.geometry_and_trigonometry.total >= 7 },
                { name: 'Total Math', current: data.math.total, correct: data.math.correct, incorrect: data.math.incorrect,unanswered: data.math.unanswered, required: 44, isMet: data.math.total >= 44 },
               
            ];

            const englishReqs = [
                { name: 'Reading', current: data.reading.total, correct: data.reading.correct, incorrect: data.reading.incorrect,unanswered: data.reading.unanswered, required: 26, isMet: data.reading.total >= 26 },
                { name: 'Writing', current: data.writing.total, correct: data.writing.correct, incorrect: data.writing.incorrect,unanswered: data.writing.unanswered, required: 18, isMet: data.writing.total >= 18 },
                { name: 'Vocablary In Context', current: data.sentence_completion.total, correct: data.sentence_completion.correct, incorrect: data.sentence_completion.incorrect,unanswered: data.sentence_completion.unanswered, required: 10, isMet: data.sentence_completion.total >= 10 },
                { name: 'Total English', current: data.english.total, correct: data.english.correct, incorrect: data.english.incorrect,unanswered: data.english.unanswered, required: 54, isMet: data.english.total >= 54 },
              ];

            let mathScore = null;
            if (mathReqs.every(r => r.isMet)) {
                mathScore = convertCorrectAnswersToSAT(data.math.correct || 0, 'math');
            }

            let englishScore = null;
            if (englishReqs.every(r => r.isMet)) {
                englishScore = convertCorrectAnswersToSAT(data.english.correct || 0, 'english');
            }
            
            return {
                date: new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                mathScore,
                englishScore,
                mathReqs,
                englishReqs,
            };
        });
        
        setDailySatScores(processedDailyScores);

        if (processedDailyScores.length > 0) {
            setSelectedDayStats(processedDailyScores[processedDailyScores.length - 1]);
        }
        setStreaks(userProfileRes.data);
      })
      .catch(error => {
        console.error('Error fetching analytics data:', error);
        setAnswerHistory([]);
      })
      .finally(() => {
        setIsLoading(false);
    });
  }, [user_id]);

  // Fetch highest SAT score when selectedDayStats changes and has scores
  useEffect(() => {
    if (selectedDayStats) {
      const score_math = selectedDayStats.mathScore !== null ? selectedDayStats.mathScore : 0;
      const score_english = selectedDayStats.englishScore !== null ? selectedDayStats.englishScore : 0;
      api.post('/api/user/highest_sat_score/', {
        score_math,
        score_english,
      }).then(res => {
        setHighestSatScore(res.data);
      }).catch(() => setHighestSatScore(null));
    }
  }, [selectedDayStats, user_id]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center text-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-24 pl-0 md:pl-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="inline-block mb-4"
          >
            <ChartBar className="w-16 h-16 text-cyan-500" />
          </motion.div>
          <h1 className="text-2xl font-bold text-gray-600 dark:text-gray-300 font-zonaProRegular">Loading Analytics...</h1>
        </motion.div>
      </div>
    );
  }

  const isPremium = user ? user.is_premium === true : false;
  const trialUsed = user ? (user.trial_used === true) : false;
  if (!isPremium) {
    return (
      <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-center min-h-screen">
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-3xl p-8 text-center shadow-2xl border border-white/20 dark:border-gray-700/50 max-w-md"
            >
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="inline-block mb-4"
              >
                <Trophy className="w-12 h-12 text-yellow-400" />
              </motion.div>
              <h3 className="text-2xl font-zonaProUltra text-gray-800 dark:text-gray-200 mb-2">Subscription Required</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-6 font-zonaProRegular">
                {trialUsed 
                  ? 'Subscribe to access Analytics.'
                  : 'Start your 7-day free trial to access Analytics.'}
              </p>
              <motion.button
                onClick={() => navigate('/subscription')}
                className="py-3 px-8 bg-[#34c759] text-white text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {trialUsed ? 'Subscribe' : 'Start Free Trial'}
              </motion.button>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
  }

  if (!answerHistory || answerHistory.length === 0) {
    return (
      <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 flex flex-col justify-center items-center text-center min-h-screen">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="inline-block mb-6"
            >
              <ChartBar className="w-20 h-20 text-cyan-500" />
            </motion.div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-4 font-zonaProUltra">No Questions Attempted Yet!</h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 font-zonaProRegular">
              It looks like you haven't played any game. Start playing to see your analytics.
            </p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/options"
                className="inline-block py-3 px-8 text-white text-xl font-zonaProUltra rounded-full hover:shadow-lg transition-all"
                style={{ background: 'linear-gradient(to right, #34c759, #34c759)', boxShadow: '0 10px 40px rgba(52, 199, 89, 0.5)' }}
              >
                Play Now
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Custom label for Pie chart to break long names into two lines
  const pieLabel = ({ name, percent, x, y, textAnchor }) => {
    // Split long names into two lines at the first space after 10 chars
    if (name==='Geometry and Trigonometry' || name==='Problem Solving and Data Analysis') {
        const name1 = name==='Geometry and Trigonometry' ? 'Geometry' : 'Problem Solving';
        return (
          <text
            x={x}
            y={y}
            textAnchor={textAnchor}
            fill="#b59f6a"
            fontSize="10"
            fontFamily="inherit"
          >
            {name1} {Math.round(percent * 100)}%
          </text>
        );
      }
  
    return (
      <text
        x={x}
        y={y}
        textAnchor={textAnchor}
        fill="#b59f6a"
        fontSize="10"
        fontFamily="inherit"
      >
        {name} {Math.round(percent * 100)}%
      </text>
    );
  };

  // Helper for time range labels
  const timeRangeLabels = {
    "7d": "past week",
    "30d": "past month",
    "90d": "past three months",
    "365d": "past year"
  };
  const selectedLabel = timeRangeLabels[timeRange] || "";

  // Filter answerHistory for selected time range
  const now = new Date();
  let startDate = new Date();
  if (timeRange === "7d") startDate.setDate(now.getDate() - 6);
  else if (timeRange === "30d") startDate.setDate(now.getDate() - 29);
  else if (timeRange === "90d") startDate.setDate(now.getDate() - 89);
  else if (timeRange === "365d") startDate.setDate(now.getDate() - 364);
  else startDate = null;

  const filteredAnswerHistory = startDate
    ? answerHistory.filter(a => a.created_date && new Date(a.created_date) >= startDate)
    : answerHistory;

  // Recalculate totalTimePerCategory for filtered data
  const filteredCategoryData = filteredAnswerHistory.reduce((acc, ans) => {
    const category = ans.content_name || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = {
        name: category,
        correct: 0,
        incorrect: 0,
        unanswered: 0,
        totalTime: 0,
        questionCount: 0,
      };
    }
    acc[category].questionCount++;
    acc[category].totalTime += ans.time_spent || 0;
    if (ans.question_status === 'correct') acc[category].correct++;
    else if (ans.question_status === 'incorrect') acc[category].incorrect++;
    else acc[category].unanswered++;
    return acc;
  }, {});
  const filteredTimePerCategory = Object.values(filteredCategoryData).map(cat => ({
    name: cat.name,
    value: cat.totalTime,
    avgTimePerQuestion: cat.questionCount > 0 ? cat.totalTime / cat.questionCount : 0
  }));

  // Custom label for Highest SAT Score bar chart
  const highestSatScoreLabel = (props) => {
    const { x, y, width, value } = props;
    console.log(value, 'value');
    return (
      <text
        x={x + width / 2}
        y={y - 8}
        textAnchor="middle"
        fill="#6b7280"
        fontSize={16}
        fontWeight={600}
      >
        {value === 0 || value === null || value === undefined || value === '' ? "Locked" : value}
      </text>
    );
  };

  return (
    <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Main Content */}
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6">
        {/* Animated Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8"
        >
          <h1
            key={`title-${isDarkMode}`}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              color: isDarkMode ? '#ff7a00' : '#212D82',
              background: `linear-gradient(90deg, ${isDarkMode ? '#ff7a00' : '#212D82'} 0%, ${isDarkMode ? '#ff7a00' : '#212D82'} 40%, rgba(255, 122, 0, 0.8) 50%, ${isDarkMode ? '#ff7a00' : '#212D82'} 60%, ${isDarkMode ? '#ff7a00' : '#212D82'} 100%)`,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Analytics Dashboard
          </h1>
          <motion.p
            className="text-gray-600 dark:text-gray-400 text-lg font-zonaProRegular"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Track your progress and performance insights
          </motion.p>
        </motion.div>

        {/* Overall Performance Section - Redesigned */}
        <section className="mb-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Pie Chart Card */}
            <OverallPerformancePieCard
              correct={correctCount}
              wrong={incorrectCount}
              unanswered={unansweredCount}
              total={answerHistory.length}
              delay={0.2}
            />
            {/* Right: 2x2 grid of metric cards */}
            <div className="grid grid-cols-2 grid-rows-2 gap-6 w-full">
              <MetricCard
                title="SAT Score"
                value={satScoreValue}
                color="#34c759"
                icon={ChartBar}
                delay={0.3}
              />
              <MetricCard
                title="Overall Accuracy"
                value={answerHistory.length > 0 ? ((correctCount / (correctCount + incorrectCount+ unansweredCount)) * 100).toFixed(1) : 0}
                unit="%"
                color="#34c759"
                icon={Target}
                delay={0.4}
              />
              <MetricCard
                title="AVG Time/Q"
                value={answerHistory.length > 0 ? Math.round(answerHistory.reduce((acc, answer) => acc + (answer.time_spent || 0), 0) / answerHistory.length) : 0}
                unit="s"
                color="text-purple-500"
                icon={Clock}
                delay={0.5}
              />
              <MetricCard
                title="Total Play time"
                value={`${Math.floor(answerHistory.reduce((acc, answer) => acc + (answer.time_spent || 0), 0) / 3600)} h ${Math.floor((answerHistory.reduce((acc, answer) => acc + (answer.time_spent || 0), 0) % 3600) / 60)} min`}
                icon={Clock}
                color="text-orange-500"
                delay={0.6}
              />
            </div>
          </div>
        </section>

        {/* Estimated SAT Score Section - Redesigned */}
        <section className="mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-1 text-left font-zonaProUltra">Estimated SAT score</h2>
            <div className="text-gray-600 dark:text-gray-400 mb-4 text-left font-zonaProRegular">based on most recent gaming questions</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Math Score Card */}
              {selectedDayStats && (
                <ScoreCard
                  locked={selectedDayStats.mathScore === null}
                  score={selectedDayStats.mathScore}
                  title="Math Score"
                  message={selectedDayStats.mathScore === null ? 'You need to complete all 44 Math questions.' : "Great job! You've unlocked your Math score."}
                  subcategories={selectedDayStats.mathReqs.map(req => ({
                    name: req.name,
                    correct: req.correct,
                    incorrect: req.incorrect,
                    unanswered: req.unanswered,
                    total: req.current,
                    required: req.required,
                  }))}
                  totalRequired={44}
                  totalAnswered={selectedDayStats.mathReqs.reduce((a, b) => a + b.current, 0)}
                  missingSubcategories={selectedDayStats.mathReqs.slice(0, -1).filter(req => req.current < req.required).map(req => ({ name: req.name, missing: req.required - req.current }))}
                  color="yellow"
                  allComplete={selectedDayStats.mathReqs.every(req => req.current >= req.required)}
                  isMath={true}
                  delay={0.8}
                />
              )}
              {/* English Score Card */}
              {selectedDayStats && (
                <ScoreCard
                  locked={selectedDayStats.englishScore === null}
                  score={selectedDayStats.englishScore}
                  title="English Score"
                  message={selectedDayStats.englishScore === null ? 'You need to complete all 54 English questions.' : "Great job! You've unlocked your English score."}
                  subcategories={selectedDayStats.englishReqs.map(req => ({
                    name: req.name,
                    correct: req.correct,
                    incorrect: req.incorrect,
                    unanswered: req.unanswered,
                    total: req.current,
                    required: req.required,
                  }))}
                  totalRequired={54}
                  totalAnswered={selectedDayStats.englishReqs.reduce((a, b) => a + b.current, 0)}
                  missingSubcategories={selectedDayStats.englishReqs.slice(0, -1).filter(req => req.current < req.required).map(req => ({ name: req.name, missing: req.required - req.current }))}
                  color="green"
                  allComplete={selectedDayStats.englishReqs.every(req => req.current >= req.required)}
                  isMath={false}
                  delay={0.9}
                />
              )}
            </div>
          </motion.div>
        </section>

        {/* Performance by Category Section - Styled to match Estimated SAT Score tables */}
        <section className="mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
          >
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-0 text-left font-zonaProUltra">Overall Performance Breakdown</h2>
            <div className="text-gray-600 dark:text-gray-400 mb-4 text-left font-zonaProRegular">Your Performance by Subject</div>
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 items-stretch">
              <Card className="lg:col-span-7 h-full flex flex-col" delay={1.1}>
                <div className="overflow-x-auto">
                  <table className="w-full text-center rounded-2xl overflow-hidden border-separate border-spacing-0 dark:bg-gray-800/50" style={{ height: 'auto' }}>
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700 text-black dark:text-gray-200 text-base font-zonaProRegular">
                        <th className="py-2 px-2 font-bold">Game Module</th>
                        <th className="py-2 px-2 font-bold">Correct</th>
                        <th className="py-2 px-2 font-bold">Incorrect</th>
                        <th className="py-2 px-2 font-bold">Skipped</th>
                        <th className="py-2 px-2 font-bold">Total Questions</th>
                        <th className="py-2 px-2 font-bold">Accuracy</th>
                        <th className="py-2 px-2 font-bold">Strength</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryPerformance.filter(cat => cat.name === "All Content").map((cat, idx) => {
                        const accuracy = cat.attempted > 0 ? Math.round((cat.correct / cat.attempted) * 100) : 0;
                        const isStrong = accuracy >= 70;
                        return (
                          <motion.tr
                            key={cat.name}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.2 + idx * 0.05 }}
                            className="border-b last:border-b-0 dark:border-gray-700"
                          >
                            <td className="py-2 px-2 text-left text-gray-800 dark:text-gray-200">{cat.name}</td>
                            <td className="py-2 px-2 text-green-600 dark:text-green-400">{cat.correct}</td>
                            <td className="py-2 px-2 text-red-500 dark:text-red-400">{cat.incorrect}</td>
                            <td className="py-2 px-2 text-yellow-500 dark:text-yellow-400">{cat.unanswered}</td>
                            <td className="py-2 px-2 text-gray-800 dark:text-gray-200">{cat.attempted}</td>
                            <td className="py-2 px-2 text-gray-800 dark:text-gray-200">{accuracy} %</td>
                            <td className="py-2 px-2">{isStrong ? (
                              <span className="bg-lime-400/80 dark:bg-lime-500 text-white px-4 py-1 rounded-xl font-semibold">Strong</span>
                            ) : (
                              <span className="bg-red-500 dark:bg-red-600 text-white px-4 py-1 rounded-xl font-semibold">Weak</span>
                            )}</td>
                          </motion.tr>
                        );
                      })}
                      {/* Math Section Header */}
                      <tr className="bg-blue-100 dark:bg-blue-900/30">
                        <td className="py-2 px-2 text-left font-bold text-gray-800 dark:text-gray-200" colSpan={1}>Math</td>
                      </tr>
                {/* Math Subtopics */}
                {(() => {
                  const mathOrder = [
                    "All Math",
                    "Advanced math",
                    "Algebra",
                    "Geometry and Trigonometry",
                    "Problem Solving and Data Analysis"
                  ];
                  const mathCats = categoryPerformance.filter(cat =>
                    mathOrder.includes(cat.name)
                  );
                  // Sort so All Math is always first, then the rest in order
                  const sortedMathCats = mathCats.sort(
                    (a, b) => mathOrder.indexOf(a.name) - mathOrder.indexOf(b.name)
                  );
                      return sortedMathCats.map((cat, idx) => {
                        const accuracy = cat.attempted > 0 ? Math.round((cat.correct / cat.attempted) * 100) : 0;
                        const isStrong = accuracy >= 70;
                        return (
                          <motion.tr
                            key={cat.name}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.3 + idx * 0.05 }}
                            className="border-b last:border-b-0 dark:border-gray-700"
                          >
                            <td className="py-2 px-2 text-left font-medium text-gray-800 dark:text-gray-200">{cat.name}</td>
                            <td className="py-2 px-2 text-green-600 dark:text-green-400 font-medium">{cat.correct}</td>
                            <td className="py-2 px-2 text-red-500 dark:text-red-400 font-medium">{cat.incorrect}</td>
                            <td className="py-2 px-2 text-yellow-500 dark:text-yellow-400 font-medium">{cat.unanswered}</td>
                            <td className="py-2 px-2 font-medium text-gray-800 dark:text-gray-200">{cat.attempted}</td>
                            <td className="py-2 px-2 font-bold text-gray-800 dark:text-gray-200">{accuracy} %</td>
                            <td className="py-2 px-2">{isStrong ? (
                              <span className="bg-lime-400/80 dark:bg-lime-500 text-white px-4 py-1 rounded-xl font-semibold">Strong</span>
                            ) : (
                              <span className="bg-red-500 dark:bg-red-600 text-white px-4 py-1 rounded-xl font-semibold">Weak</span>
                            )}</td>
                          </motion.tr>
                        );
                      });
                })()}
                {/* Total Math Row */}
                {(() => {
                  console.log(categoryPerformance, 'categoryPerformance');
                  const mathCats = categoryPerformance.filter(cat =>
                    ["All Math", "Advanced math", "Algebra", "Geometry and Trigonometry", "Problem Solving and Data Analysis"].includes(cat.name)
                  );
                  console.log(mathCats, 'mathCats');
                  const totalCorrect = mathCats.reduce((sum, cat) => sum + cat.correct, 0);
                  const totalIncorrect = mathCats.reduce((sum, cat) => sum + cat.incorrect, 0);
                  const totalUnanswered = mathCats.reduce((sum, cat) => sum + cat.unanswered, 0);
                  const totalAttempted = mathCats.reduce((sum, cat) => sum + cat.attempted, 0);
                      const accuracy = totalAttempted > 0 ? Math.round((totalCorrect / totalAttempted) * 100) : 0;
                      const isStrong = accuracy >= 70;
                      return (
                        <motion.tr
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 1.4 }}
                          className="font-bold dark:bg-gray-800/30"
                        >
                          <td className="py-2 px-2 text-left text-gray-800 dark:text-gray-200">Overall Math Performance</td>
                          <td className="py-2 px-2 text-green-600 dark:text-green-400">{totalCorrect}</td>
                          <td className="py-2 px-2 text-red-500 dark:text-red-400">{totalIncorrect}</td>
                          <td className="py-2 px-2 text-yellow-500 dark:text-yellow-400">{totalUnanswered}</td>
                          <td className="py-2 px-2 text-gray-800 dark:text-gray-200">{totalAttempted}</td>
                          <td className="py-2 px-2 text-gray-800 dark:text-gray-200">{accuracy} %</td>
                          <td className="py-2 px-2">{isStrong ? (
                            <span className="bg-lime-400/80 dark:bg-lime-500 text-white px-4 py-1 rounded-xl font-semibold">Strong</span>
                          ) : (
                            <span className="bg-red-500 dark:bg-red-600 text-white px-4 py-1 rounded-xl font-semibold">Weak</span>
                          )}</td>
                        </motion.tr>
                      );
                })()}
                      {/* English Section Header */}
                      <tr className="bg-yellow-100 dark:bg-yellow-900/30">
                        <td className="py-2 px-2 text-left font-bold text-gray-800 dark:text-gray-200" colSpan={1}>English</td>
                      </tr>
                      {/* English Subtopics */}
                      {categoryPerformance.filter(cat =>
                        ["All English", "Writing", "Reading", "Vocablary In Context"].includes(cat.name)
                      ).map((cat, idx) => {
                        const accuracy = cat.attempted > 0 ? Math.round((cat.correct / cat.attempted) * 100) : 0;
                        const isStrong = accuracy >= 70;
                        return (
                          <motion.tr
                            key={cat.name}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.5 + idx * 0.05 }}
                            className="border-b last:border-b-0 dark:border-gray-700"
                          >
                            <td className="py-2 px-2 text-left font-medium text-gray-800 dark:text-gray-200">{cat.name}</td>
                            <td className="py-2 px-2 text-green-600 dark:text-green-400 font-medium">{cat.correct}</td>
                            <td className="py-2 px-2 text-red-500 dark:text-red-400 font-medium">{cat.incorrect}</td>
                            <td className="py-2 px-2 text-yellow-500 dark:text-yellow-400 font-medium">{cat.unanswered}</td>
                            <td className="py-2 px-2 font-medium text-gray-800 dark:text-gray-200">{cat.attempted}</td>
                            <td className="py-2 px-2 font-bold text-gray-800 dark:text-gray-200">{accuracy} %</td>
                            <td className="py-2 px-2">{isStrong ? (
                              <span className="bg-lime-400/80 dark:bg-lime-500 text-white px-4 py-1 rounded-xl font-semibold">Strong</span>
                            ) : (
                              <span className="bg-red-500 dark:bg-red-600 text-white px-4 py-1 rounded-xl font-semibold">Weak</span>
                            )}</td>
                          </motion.tr>
                        );
                      })}
                {/* Total English Row */}
                {(() => {
                  const engCats = categoryPerformance.filter(cat =>
                    ["All English", "Writing", "Reading", "Vocablary In Context"].includes(cat.name)
                  );
                  const totalCorrect = engCats.reduce((sum, cat) => sum + cat.correct, 0);
                  const totalIncorrect = engCats.reduce((sum, cat) => sum + cat.incorrect, 0);
                  const totalUnanswered = engCats.reduce((sum, cat) => sum + cat.unanswered, 0);
                  const totalAttempted = engCats.reduce((sum, cat) => sum + cat.attempted, 0);
                      const accuracy = totalAttempted > 0 ? Math.round((totalCorrect / totalAttempted) * 100) : 0;
                      const isStrong = accuracy >= 70;
                      return (
                        <motion.tr
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 1.6 }}
                          className="font-bold dark:bg-gray-800/30"
                        >
                          <td className="py-2 px-2 text-left text-gray-800 dark:text-gray-200">Overall English Performance</td>
                          <td className="py-2 px-2 text-green-600 dark:text-green-400">{totalCorrect}</td>
                          <td className="py-2 px-2 text-red-500 dark:text-red-400">{totalIncorrect}</td>
                          <td className="py-2 px-2 text-yellow-500 dark:text-yellow-400">{totalUnanswered}</td>
                          <td className="py-2 px-2 text-gray-800 dark:text-gray-200">{totalAttempted}</td>
                          <td className="py-2 px-2 text-gray-800 dark:text-gray-200">{accuracy} %</td>
                          <td className="py-2 px-2">{isStrong ? (
                            <span className="bg-lime-400/80 dark:bg-lime-500 text-white px-4 py-1 rounded-xl font-semibold">Strong</span>
                          ) : (
                            <span className="bg-red-500 dark:bg-red-600 text-white px-4 py-1 rounded-xl font-semibold">Weak</span>
                          )}</td>
                        </motion.tr>
                      );
                    })()}
                    </tbody>
                  </table>
                </div>
              </Card>
              
              {/* Highest SAT Score Card */}
              <Card className="lg:col-span-3 h-full flex flex-col" delay={1.2}>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 text-left font-zonaProUltra">Highest SAT Score</h3>
                <h4 className="text-sm text-gray-600 dark:text-gray-400 text-left font-zonaProRegular">based on gaming questions</h4>
            <div className="flex-1 flex items-center">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={highestSatScore ? [
                    {
                      name: 'Math',
                      score: highestSatScore.highest_score_math,
                      date: highestSatScore.sat_score_math_updated_time ? new Date(highestSatScore.sat_score_math_updated_time).toLocaleDateString('en-US', { month: 'short', day: '2-digit' }) : '',
                    },
                    {
                      name: 'English',
                      score: highestSatScore.highest_score_english,
                      date: highestSatScore.sat_score_english_updated_time ? new Date(highestSatScore.sat_score_english_updated_time).toLocaleDateString('en-US', { month: 'short', day: '2-digit' }) : '',
                    },
                  ] : [
                    { name: 'Math', score: 0, date: '' },
                    { name: 'English', score: 0, date: '' },
                  ]}
                  margin={{ top: 30, right: 20, left: 10, bottom: 20 }}
                >
                  <XAxis dataKey="name" />
                  <YAxis domain={[0, 800]} />
                  <Tooltip 
                    formatter={(value) => [`${value}`, 'Score']}
                    labelFormatter={(label) => `Subject: ${label}`}
                  />
                  <Bar dataKey="score">
                    <LabelList dataKey="date" content={highestSatScoreLabel} />
                    <Cell fill="#60a5fa" /> {/* Math: blue */}
                    <Cell fill="#facc15" /> {/* English: yellow */}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
            </div>
          </motion.div>
        </section>

        {/* Time-Based Insights */}
        <section className="mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3 }}
            className="flex justify-between items-center mb-4"
          >
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4 text-left font-zonaProUltra">Time-Based Insights</h2>
            <motion.select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              whileHover={{ scale: 1.05 }}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-zonaProRegular"
            >
              <option value="7d">Past Week</option>
              <option value="30d">Past Month</option>
              <option value="90d">Past Three Months</option>
              <option value="365d">Past Year</option>
            </motion.select>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card delay={1.4}>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 text-left font-zonaProUltra">
                Performance Trend for {selectedLabel}
              </h3>
            {timeRange === "7d" && (
              <ResponsiveContainer width="100%" height={400}>
              <LineChart data={recentAccuracyTrend} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <XAxis 
                  dataKey="label" 
                  style={{ fill: '#4b5563' }}
                />
                <YAxis unit="%" domain={[0, 100]} style={{ fill: '#4b5563' }} />
                <Tooltip
                  formatter={(value) => value !== null ? `${value.toFixed(1)}% Accuracy` : 'No data'}
                  labelFormatter={(label) => `Date: ${label}`}
                  wrapperStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.5rem', color: '#4b5563' }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="accuracy" 
                  stroke="#ef4444" 
                  activeDot={{ r: 8 }} 
                  strokeWidth={2}
                  connectNulls={true}
                />
              </LineChart>
            </ResponsiveContainer>
            )}
            {timeRange === "30d" && (
              <ResponsiveContainer width="100%" height={400}>
              <LineChart data={recentAccuracyTrend_month} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <XAxis 
                  dataKey="label" 
                  style={{ fill: '#4b5563' }}
                />
                <YAxis unit="%" domain={[0, 100]} style={{ fill: '#4b5563' }} />
                <Tooltip
                  formatter={(value) => value !== null ? `${value.toFixed(1)}% Accuracy` : 'No data'}
                  labelFormatter={(label) => `Week: ${label}`}
                  wrapperStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.5rem', color: '#4b5563' }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="accuracy" 
                  stroke="#ef4444" 
                  activeDot={{ r: 8 }} 
                  strokeWidth={2}
                  connectNulls={true}
                />
              </LineChart>
            </ResponsiveContainer>
            )}
            {timeRange === "90d" && (
              <ResponsiveContainer width="100%" height={400}>
              <LineChart data={recentAccuracyTrend_months} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <XAxis 
                  dataKey="label" 
                  style={{ fill: '#4b5563' }}
                />
                <YAxis unit="%" domain={[0, 100]} style={{ fill: '#4b5563' }} />
                <Tooltip
                  formatter={(value) => value !== null ? `${value.toFixed(1)}% Accuracy` : 'No data'}
                  labelFormatter={(label) => `Month: ${label}`}
                  wrapperStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.5rem', color: '#4b5563' }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="accuracy" 
                  stroke="#ef4444" 
                  activeDot={{ r: 8 }} 
                  strokeWidth={2}
                  connectNulls={true}
                />
              </LineChart>
            </ResponsiveContainer>
            )}
            {timeRange === "365d" && (
              <ResponsiveContainer width="100%" height={400}>
              <LineChart data={recentAccuracyTrend_year} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <XAxis 
                  dataKey="label" 
                  style={{ fill: '#4b5563' }}
                />
                <YAxis unit="%" domain={[0, 100]} style={{ fill: '#4b5563' }} />
                <Tooltip
                  formatter={(value) => value !== null ? `${value.toFixed(1)}% Accuracy` : 'No data'}
                  labelFormatter={(label) => `Month: ${label}`}
                  wrapperStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.5rem', color: '#4b5563' }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="accuracy" 
                  stroke="#ef4444" 
                  activeDot={{ r: 8 }} 
                  strokeWidth={2}
                  connectNulls={true}
                />
              </LineChart>
            </ResponsiveContainer>
            )}
            </Card>
            <Card delay={1.5}>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 text-left font-zonaProUltra">
                Study Time Allocation for {selectedLabel}
              </h3>
              <h4 className="text-sm text-gray-600 dark:text-gray-400 text-left mb-2 font-zonaProRegular">
                Total Time Spent: {Math.floor(filteredTimePerCategory.reduce((acc, curr) => acc + curr.value, 0) / 3600)} h {Math.floor((filteredTimePerCategory.reduce((acc, curr) => acc + curr.value, 0) % 3600) / 60)} min
              </h4>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={filteredTimePerCategory}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  label={pieLabel}
                  labelLine={false}
                  style={{ fontSize: '0.5rem' }}
                >
                  {filteredTimePerCategory.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name, props) => [
                    `Time Spent: ${(value / 60).toFixed(1)} min`,
                    `Avg. per Q: ${props.payload.payload.avgTimePerQuestion.toFixed(1)}s`
                  ]}
                  labelFormatter={(label) => `Category: ${label}`}
                  wrapperStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.5rem', color: '#4b5563' }}
                />
                <Legend wrapperStyle={{ fontSize: '0.7rem', color: '#4b5563' }} />
              </PieChart>
            </ResponsiveContainer>
            </Card>
          </div>
        </section>

        {/* Engagement & Consistency */}
        <section className="mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6 }}
          >
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4 text-left font-zonaProUltra">Engagement & Consistency</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <MetricCard
                title="Active Days"
                value={engagement.numberOfActiveDays}
                icon={Users}
                color="text-cyan-500"
                delay={1.7}
              />
              <MetricCard
                title="Longest Correct Streak"
                value={streaks.longest_streak}
                icon={Zap}
                color="text-pink-500"
                delay={1.8}
              />
              <MetricCard
                title="Current Correct Streak"
                value={streaks.current_streak}
                icon={Zap}
                color="text-purple-500"
                delay={1.9}
              />
            </div>
          </motion.div>
        </section>
      </div>
    </div>
  );
};

export default AnalyticsPage;